import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Activity, CalendarDays, Clock3, LayoutDashboard, Trophy, Repeat2, Flame, Crown, Star, Umbrella, ShieldCheck } from 'lucide-react';
import MonthNavigation from './MonthNavigation';
import CalendarGrid from './CalendarGrid';
import VacationDateMenu from './VacationDateMenu';
import MonthlySummary from './MonthlySummary';
import DateRangeSelector from './DateRangeSelector';
import DailyTrendChart from './DailyTrendChart';
import GoalJourneyPanel from './GoalJourneyPanel';
import PracticeSummaryProgress from './PracticeSummaryProgress';
import RecordCollectionCard from './RecordCollectionCard';
import { additionalRecordArtwork } from '../services/RecordArtwork';
import totalScoreArtwork from '../assets/record-cards/total-score.webp';
import totalRepeatsArtwork from '../assets/record-cards/total-repeats.webp';
import totalTimeArtwork from '../assets/record-cards/total-time.webp';
import totalDaysArtwork from '../assets/record-cards/total-days.webp';
import bestStreakArtwork from '../assets/record-cards/best-streak.webp';
import bestDayScoreArtwork from '../assets/record-cards/best-day-score.webp';
import bestDayTimeArtwork from '../assets/record-cards/best-day-time.webp';
import tournamentScoreArtwork from '../assets/record-cards/tournament-score.webp';
import tournamentRoundArtwork from '../assets/record-cards/tournament-round.webp';
import tournamentEntriesArtwork from '../assets/record-cards/tournament-entries.webp';
import tournamentWinRateArtwork from '../assets/record-cards/tournament-win-rate.webp';
import cardUpgradesArtwork from '../assets/record-cards/card-upgrades.webp';
import marketUploadsArtwork from '../assets/record-cards/market-uploads.webp';
import listeningTimeArtwork from '../assets/record-cards/listening-time.webp';
import CalendarService from '../services/CalendarService';
import apiClient from '../services/apiClient';
import { STREAK_PROTECTION_CHANGED, createProtectionOperationId, getAccountToday } from '../services/StreakProtection';
import { formatCompactInteger, toRoundedInteger } from '../utils/numberFormat';
import { getRecordAchievementState, getRecordAchievementTiers, RECORD_ACHIEVEMENT_IDS, RECORD_ACHIEVEMENT_DEFINITIONS } from '../utils/recordAchievements';
import { t } from '../i18n';
import './CalendarPage.css';

function formatAchievementDuration(seconds = 0) {
  const unit = (key, count) => t(`accountActivity.stats.${key}Value`, undefined, { count });
  if (!seconds) return unit('minutes', 0);
  if (seconds < 60) return unit('seconds', Math.round(seconds));

  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (!hours) return unit('minutes', totalMinutes);
  if (!minutes) return unit('hours', hours);
  return `${unit('hours', hours)} ${unit('minutes', minutes)}`;
}

function formatWinRate(tournament = {}) {
  const percent = Number(tournament.winningPercent) || 0;
  return `${percent.toFixed(1)}%`;
}

function formatRecordTarget(id, threshold = RECORD_ACHIEVEMENT_DEFINITIONS.find((definition) => definition.id === id)?.threshold) {
  if (id === RECORD_ACHIEVEMENT_IDS.MEMBERSHIP_YEAR) return t(`accountActivity.stats.${threshold === 1 ? 'yearsValue' : 'multipleYearsValue'}`, undefined, { count: threshold });
  if (id === RECORD_ACHIEVEMENT_IDS.TOURNAMENT_ROUND) return formatBestRound({ bestRoundCode: 'champion' });
  if ([RECORD_ACHIEVEMENT_IDS.TOTAL_TIME, RECORD_ACHIEVEMENT_IDS.MAX_DAILY_TIME, RECORD_ACHIEVEMENT_IDS.LISTENING_TIME].includes(id)) return formatAchievementDuration(threshold);
  if (id === RECORD_ACHIEVEMENT_IDS.TOURNAMENT_WIN_RATE) return `${threshold}%`;
  return formatCompactInteger(threshold);
}

export function formatBestRound(tournament = {}) {
  const resultKeys = {
    groupStage: 'groupStage', roundOf16: 'roundOf16', quarterFinalist: 'quarterFinal',
    semiFinalist: 'semiFinal', runnerUp: 'final', champion: 'championship',
  };
  const legacyLabels = {
    roundof32: 'groupStage', groupstage: 'groupStage', roundof16: 'roundOf16',
    quarterfinal: 'quarterFinal', quarterfinalist: 'quarterFinal',
    semifinal: 'semiFinal', semifinalist: 'semiFinal',
    final: 'final', runnerup: 'final', championship: 'championship', champion: 'championship',
  };
  const label = tournament.bestRound;
  const normalized = typeof label === 'string' ? label.toLowerCase().replace(/[\s-]/g, '') : '';
  const key = Object.prototype.hasOwnProperty.call(resultKeys, tournament.bestRoundCode)
    ? resultKeys[tournament.bestRoundCode]
    : Object.prototype.hasOwnProperty.call(legacyLabels, normalized) ? legacyLabels[normalized] : null;
  return key ? t(`accountActivity.user.rounds.${key}`) : label || '-';
}

function formatDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getAvailableDaysInMonth(year, month, today = new Date()) {
  const range = getMonthDateRange(year, month);
  return getAvailableDaysInRange(range.beginDate, range.endDate, today);
}

export function getMonthDateRange(year, month) {
  return { beginDate: formatDateKey(new Date(year, month - 1, 1)), endDate: formatDateKey(new Date(year, month, 0)) };
}

export function getAvailableDaysInRange(beginDate, endDate, today = new Date()) {
  const lastDay = endDate < formatDateKey(today) ? endDate : formatDateKey(today);
  // UTC date-only arithmetic avoids 23/25-hour days at daylight-saving changes.
  const begin = Date.parse(`${beginDate}T00:00:00Z`);
  const end = Date.parse(`${lastDay}T00:00:00Z`);
  return Number.isFinite(begin) && Number.isFinite(end) ? Math.max(0, Math.floor((end - begin) / 86400000) + 1) : 0;
}

export function buildDailyTrendData(dailyStats = [], range, today = new Date()) {
  const stats = dailyStats instanceof Map ? Array.from(dailyStats.values()) : dailyStats;
  let days = Array.isArray(stats) ? stats : [];
  if (range) {
    const byDate = new Map(days.filter((stat) => stat?.date).map((stat) => [stat.date, stat]));
    const begin = Date.parse(`${range.beginDate}T00:00:00Z`);
    days = Array.from({ length: getAvailableDaysInRange(range.beginDate, range.endDate, today) }, (_, index) => {
      const date = new Date(begin + index * 86400000).toISOString().slice(0, 10);
      return byDate.get(date) || { date };
    });
  }
  return days
    .filter((stat) => stat?.date)
    .map((stat) => {
      const totalSeconds = Math.max(0, Number(stat.totalSeconds) || 0);
      const score = Math.max(0, Number(stat.totalScoreGained) || 0);
      return {
        date: stat.date,
        practiceMinutes: toRoundedInteger(totalSeconds / 60),
        practiceSeconds: totalSeconds,
        score: toRoundedInteger(score),
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((stat) => ({ ...stat, date: stat.date.slice(5) }));
}

function CalendarPage() {
  const calendarService = useMemo(() => new CalendarService(), []);
  const deviceToday = useMemo(() => new Date(), []);
  const [protectionState, setProtectionState] = useState(null);
  const today = useMemo(() => {
    if (!protectionState?.today) return deviceToday;
    const [year, month, day] = protectionState.today.split('-').map(Number);
    return new Date(year, month - 1, day);
  }, [deviceToday, protectionState?.today]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [vacationBusy, setVacationBusy] = useState(false);
  const [vacationError, setVacationError] = useState(null);
  const vacationOperation = useRef(null);
  const vacationLock = useRef(false);
  const monthNavigated = useRef(false);
  const hasAlignedMonth = useRef(false);
  const calendarTimezone = useRef(null);
  const [activeTab, setActiveTab] = useState('pace');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [dateRange, setDateRange] = useState(() => getMonthDateRange(today.getFullYear(), today.getMonth() + 1));
  const [monthlyData, setMonthlyData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [isCalendarLoading, setIsCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  const [calendarReloadToken, setCalendarReloadToken] = useState(0);
  const [accountSummary, setAccountSummary] = useState(null);
  const [accountRecords, setAccountRecords] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  const [summaryReloadToken, setSummaryReloadToken] = useState(0);
  const [monthlyTrendStats, setMonthlyTrendStats] = useState([]);
  const [isPaceLoading, setIsPaceLoading] = useState(false);
  const [paceError, setPaceError] = useState(null);
  const [paceLoaded, setPaceLoaded] = useState(null);
  const [paceReloadToken, setPaceReloadToken] = useState(0);
  const [customRangeStats, setCustomRangeStats] = useState(null);
  const [isCustomRangeLoading, setIsCustomRangeLoading] = useState(false);
  const [customRangeError, setCustomRangeError] = useState(null);
  const [rangeReloadToken, setRangeReloadToken] = useState(0);
  const monthRange = getMonthDateRange(year, month);
  const isMonthRange = dateRange.beginDate === monthRange.beginDate && dateRange.endDate === monthRange.endDate;
  const isRangeValid = Boolean(dateRange.beginDate && dateRange.endDate && dateRange.beginDate <= dateRange.endDate);

  useEffect(() => {
    if (summaryLoaded) return undefined;
    let isCurrent = true;

    const loadSummary = async () => {
      setIsSummaryLoading(true);
      setSummaryError(null);
      try {
        const profile = await apiClient.getCurrentUserSummary();
        if (!isCurrent) return;
        setAccountSummary(profile?.summary || {});
        setAccountRecords({
          achievements: profile?.achievements || {},
          tournament: profile?.tournament || {},
        });
        setSummaryLoaded(true);
      } catch (error) {
        if (!isCurrent) return;
        console.error('Error fetching account summary:', error);
        setSummaryError(error.message || t('accountActivity.calendar.summaryFailed'));
      } finally {
        if (isCurrent) setIsSummaryLoading(false);
      }
    };

    loadSummary();
    return () => { isCurrent = false; };
  }, [summaryLoaded, summaryReloadToken]);

  useEffect(() => {
    if (activeTab !== 'calendar' || !isMonthRange || !isRangeValid ||
      (paceLoaded?.beginDate === dateRange.beginDate && paceLoaded?.endDate === dateRange.endDate)) return undefined;
    let isCurrent = true;

    const loadPace = async () => {
      setIsPaceLoading(true);
      setPaceError(null);
      try {
        const { beginDate, endDate } = dateRange;
        const trend = await calendarService.getDateRangeStats(beginDate, endDate);
        if (!isCurrent) return;
        if (!Array.isArray(trend?.dailyStats)) throw new Error(t('accountActivity.calendar.paceFailed'));
        setMonthlyTrendStats(trend.dailyStats);
        setPaceLoaded({ beginDate, endDate });
      } catch (error) {
        if (!isCurrent) return;
        console.error('Error fetching pace data:', error);
        setPaceError(error.message || t('accountActivity.calendar.paceFailed'));
      } finally {
        if (isCurrent) setIsPaceLoading(false);
      }
    };

    loadPace();
    return () => { isCurrent = false; };
  }, [activeTab, calendarService, dateRange, isMonthRange, isRangeValid, paceLoaded, paceReloadToken]);

  useEffect(() => {
    if (activeTab !== 'calendar') return undefined;
    let isCurrent = true;

    const loadMonth = async () => {
      setIsCalendarLoading(true);
      setCalendarError(null);
      setMonthlyData(null);
      setSummary(null);
      try {
        const data = await calendarService.getMonthlyStats(year, month, null, { force: true });
        if (!isCurrent) return;
        setProtectionState(data?.enabled && data?.today && data?.timezone ? data : null);
        if (data?.enabled && data?.timezone && calendarTimezone.current !== data.timezone) {
          calendarTimezone.current = data.timezone;
          // The first parallel trend read may have used the device zone before the account pin arrived.
          setPaceLoaded(null);
          setPaceReloadToken(token => token + 1);
          setRangeReloadToken(token => token + 1);
        }
        if (!hasAlignedMonth.current && data?.enabled && data?.today) {
          hasAlignedMonth.current = true;
          const [accountYear, accountMonth] = data.today.split('-').map(Number);
          if (!monthNavigated.current && (accountYear !== year || accountMonth !== month)) {
            setYear(accountYear);
            setMonth(accountMonth);
            setDateRange(getMonthDateRange(accountYear, accountMonth));
            return;
          }
        }
        setMonthlyData(new Map(
          (Array.isArray(data?.dailyStats) ? data.dailyStats : [])
            .map((stat) => [stat.date, stat])
        ));
        setSummary(data?.summary || {
          practicedDays: 0,
          totalSeconds: 0,
          totalRepeatCount: 0,
          totalScoreGained: 0,
        });
      } catch (error) {
        if (!isCurrent) return;
        console.error('Error fetching monthly data:', error);
        setCalendarError(error.message || t('accountActivity.calendar.calendarFailed'));
      } finally {
        if (isCurrent) setIsCalendarLoading(false);
      }
    };

    loadMonth();
    return () => { isCurrent = false; };
  }, [activeTab, calendarReloadToken, calendarService, month, year]);

  useEffect(() => {
    if (activeTab !== 'calendar' || !isRangeValid || isMonthRange) return undefined;
    let isCurrent = true;
    const { beginDate, endDate } = dateRange;
    setIsCustomRangeLoading(true);
    setCustomRangeError(null);
    setCustomRangeStats(null);
    calendarService.getDateRangeStats(beginDate, endDate).then((data) => {
      if (!Array.isArray(data?.dailyStats)) throw new Error(t('accountActivity.calendar.rangeFailed'));
      if (isCurrent) setCustomRangeStats({ ...(data.summary || {}), dailyStats: data.dailyStats, beginDate, endDate,
        totalDays: getAvailableDaysInRange(beginDate, endDate, today) });
    }).catch((error) => {
      if (!isCurrent) return;
      console.error('Error fetching date range stats:', error);
      setCustomRangeError(error.message || t('accountActivity.calendar.rangeFailed'));
    }).finally(() => { if (isCurrent) setIsCustomRangeLoading(false); });
    return () => { isCurrent = false; };
  }, [activeTab, calendarService, dateRange, isMonthRange, isRangeValid, rangeReloadToken, today]);

  useEffect(() => {
    const refresh = () => {
      if (document.hidden) return;
      calendarService.clearCache?.();
      setCalendarReloadToken(token => token + 1);
      setSummaryLoaded(false);
      setPaceLoaded(null);
      setPaceReloadToken(token => token + 1);
      setRangeReloadToken(token => token + 1);
    };
    const visibility = () => { if (!document.hidden) refresh(); };
    window.addEventListener('focus', refresh);
    window.addEventListener(STREAK_PROTECTION_CHANGED, refresh);
    window.addEventListener('practiceSessionComplete', refresh);
    document.addEventListener('visibilitychange', visibility);
    const timer = window.setInterval(() => {
      if (protectionState?.timezone && getAccountToday(protectionState.timezone) !== protectionState.today) refresh();
    }, 60_000);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener(STREAK_PROTECTION_CHANGED, refresh);
      window.removeEventListener('practiceSessionComplete', refresh);
      document.removeEventListener('visibilitychange', visibility);
      window.clearInterval(timer);
    };
  }, [calendarService, protectionState?.timezone, protectionState?.today]);

  const closeDateMenu = useCallback(() => {
    if (!vacationLock.current) setSelectedDate(null);
  }, []);

  const selectDate = date => {
    if (vacationLock.current) return;
    setSelectedDate(current => current === date ? null : date);
    setVacationError(null);
    if (vacationOperation.current?.date !== date) vacationOperation.current = null;
  };

  const updateVacation = async action => {
    if (vacationLock.current || !selectedDate || !protectionState?.enabled) return;
    vacationLock.current = true;
    setVacationBusy(true);
    setVacationError(null);
    if (vacationOperation.current?.date !== selectedDate || vacationOperation.current?.action !== action) {
      vacationOperation.current = { date: selectedDate, action, id: createProtectionOperationId() };
    }
    try {
      await apiClient[action === 'cancel' ? 'cancelVacation' : 'reserveVacation'](selectedDate, vacationOperation.current.id);
      vacationOperation.current = null;
      setSelectedDate(null);
      calendarService.clearCache?.();
      setCalendarReloadToken(token => token + 1);
    } catch (error) {
      setVacationError(error.message || t('streakProtection.saveFailed'));
    } finally {
      vacationLock.current = false;
      setVacationBusy(false);
    }
  };

  const handleMonthChange = (nextMonth, nextYear = year) => {
    monthNavigated.current = true;
    setSelectedDate(null);
    setVacationError(null);
    setMonth(nextMonth);
    setYear(nextYear);
    setDateRange(getMonthDateRange(nextYear, nextMonth));
  };

  const handlePreviousMonth = () => {
    if (month === 1) {
      handleMonthChange(12, year - 1);
    } else {
      handleMonthChange(month - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 12) {
      handleMonthChange(1, year + 1);
    } else {
      handleMonthChange(month + 1);
    }
  };

  const trendStats = isMonthRange ? monthlyTrendStats : customRangeStats?.dailyStats;
  const trendData = useMemo(() => buildDailyTrendData(trendStats, dateRange, today), [trendStats, dateRange, today]);
  const trendRange = isMonthRange ? paceLoaded : customRangeStats;
  const isTrendCurrent = isRangeValid && trendRange?.beginDate === dateRange.beginDate && trendRange?.endDate === dateRange.endDate;
  const isTrendLoading = isMonthRange ? isPaceLoading : isCustomRangeLoading;
  const trendError = isMonthRange ? paceError : customRangeError;
  const monthlySummary = useMemo(() => ({
    ...(summary || {}),
    totalDays: getAvailableDaysInMonth(year, month, today),
  }), [month, summary, today, year]);
  const practiceSummaryProgress = accountSummary?.practiceSummaryProgress || {
    practicing: Math.max(0,
      Number(accountSummary?.totalCardCount || 0)
      - Number(accountSummary?.graduatedCardCount ?? accountSummary?.masteredCardCount ?? 0)
    ),
    testing: 0,
    completed: Number(accountSummary?.graduatedCardCount ?? accountSummary?.masteredCardCount ?? 0),
  };
  const achievements = accountRecords?.achievements || {};
  const tournament = accountRecords?.tournament || {};
  const recordUnlocks = getRecordAchievementState({ summary: accountSummary || {}, achievements, tournament });
  const recordTiers = getRecordAchievementTiers({ summary: accountSummary || {}, achievements, tournament });
  const tierProps = (id) => ({
    tier: recordTiers[id] || 0,
    tierTargets: RECORD_ACHIEVEMENT_DEFINITIONS.find((definition) => definition.id === id)?.tiers?.map((target) => formatRecordTarget(id, target)),
  });

  return (
    <div className="calendar-page">
      <div className="calendar-header app-page-header">
        <div className="calendar-title-block app-page-header-copy">
          <h1 className="page-title-fx page-title-calendar">{t('accountActivity.calendar.title')}</h1>
          <p>{t('accountActivity.calendar.subtitle')}</p>
        </div>
      </div>

      {isSummaryLoading && <div className="calendar-inline-status" role="status">{t('accountActivity.calendar.loadingSummary')}</div>}
      {summaryError && <div className="calendar-error" role="alert"><p>{summaryError}</p><button type="button" onClick={() => setSummaryReloadToken((token) => token + 1)}>{t('accountActivity.retry')}</button></div>}
      {summaryLoaded && <PracticeSummaryProgress counts={practiceSummaryProgress} compact completedMaxPercent={33} showAddMoreMessage valueFormatter={formatCompactInteger} />}

      <div className="calendar-tabs" role="tablist" aria-label={t('accountActivity.calendar.views')}>
        <button type="button" role="tab" aria-selected={activeTab === 'pace'} className={activeTab === 'pace' ? 'is-active' : ''} onClick={() => setActiveTab('pace')}>
          <Activity size={18} aria-hidden="true" />
          <span>{t('accountActivity.calendar.pace')}</span>
        </button>
        <button type="button" role="tab" aria-selected={activeTab === 'calendar'} className={activeTab === 'calendar' ? 'is-active' : ''} onClick={() => setActiveTab('calendar')}>
          <CalendarDays size={18} aria-hidden="true" />
          <span>{t('accountActivity.calendar.calendar')}</span>
        </button>
        <button type="button" role="tab" aria-selected={activeTab === 'summary'} className={activeTab === 'summary' ? 'is-active' : ''} onClick={() => setActiveTab('summary')}>
          <LayoutDashboard size={18} aria-hidden="true" />
          <span>{t('accountActivity.calendar.summary')}</span>
        </button>
      </div>

      {activeTab === 'summary' && (
        <section className="calendar-tab-panel calendar-summary-panel" role="tabpanel">
          {!isSummaryLoading && !summaryError && summaryLoaded && (
            <>
              <section className="calendar-cumulative-records" aria-labelledby="calendar-cumulative-records-title">
                <h2 id="calendar-cumulative-records-title">{t('accountActivity.calendar.cumulativeRecords')}</h2>
                <div className="calendar-lifetime-stats">
                  <RecordCollectionCard
                    className="calendar-lifetime-stat"
                    artwork={totalScoreArtwork}
                    number={1}
                    {...tierProps(RECORD_ACHIEVEMENT_IDS.TOTAL_SCORE)}
                    isUnlocked={recordUnlocks[RECORD_ACHIEVEMENT_IDS.TOTAL_SCORE]}
                    targetValue={formatRecordTarget(RECORD_ACHIEVEMENT_IDS.TOTAL_SCORE)}
                    fallbackIcon={Trophy}
                    label={t('accountActivity.stats.totalGainedScore')}
                    value={formatCompactInteger(accountSummary?.totalScoreGained ?? accountSummary?.exp)}
                  />
                  <RecordCollectionCard
                    className="calendar-lifetime-stat"
                    artwork={totalRepeatsArtwork}
                    number={2}
                    {...tierProps(RECORD_ACHIEVEMENT_IDS.TOTAL_REPEATS)}
                    isUnlocked={recordUnlocks[RECORD_ACHIEVEMENT_IDS.TOTAL_REPEATS]}
                    targetValue={formatRecordTarget(RECORD_ACHIEVEMENT_IDS.TOTAL_REPEATS)}
                    fallbackIcon={Repeat2}
                    label={t('accountActivity.stats.totalRepeats')}
                    value={formatCompactInteger(accountSummary?.totalRepeatCount)}
                  />
                  <RecordCollectionCard
                    className="calendar-lifetime-stat is-practice-time"
                    artwork={totalTimeArtwork}
                    number={3}
                    {...tierProps(RECORD_ACHIEVEMENT_IDS.TOTAL_TIME)}
                    isUnlocked={recordUnlocks[RECORD_ACHIEVEMENT_IDS.TOTAL_TIME]}
                    targetValue={formatRecordTarget(RECORD_ACHIEVEMENT_IDS.TOTAL_TIME)}
                    fallbackIcon={Clock3}
                    label={t('accountActivity.stats.totalPracticeTime')}
                    valueVariant="time"
                    value={t('accountActivity.stats.hoursValue', undefined, {
                      count: Math.floor(Math.max(0, Number(accountSummary?.totalPracticeTime) || 0) / 3600),
                    })}
                  />
                  <RecordCollectionCard
                    className="calendar-lifetime-stat"
                    artwork={totalDaysArtwork}
                    number={4}
                    {...tierProps(RECORD_ACHIEVEMENT_IDS.TOTAL_DAYS)}
                    isUnlocked={recordUnlocks[RECORD_ACHIEVEMENT_IDS.TOTAL_DAYS]}
                    targetValue={formatRecordTarget(RECORD_ACHIEVEMENT_IDS.TOTAL_DAYS)}
                    fallbackIcon={CalendarDays}
                    label={t('accountActivity.stats.totalDays')}
                    value={formatCompactInteger(accountSummary?.totalDays)}
                  />
                  {[
                    ['writingTraining', 'writingTrainingCount', Repeat2, 12, cardUpgradesArtwork],
                    ['marketUploads', 'marketUploadCount', Star, 13, marketUploadsArtwork],
                    ['listeningTime', 'totalListeningTime', Clock3, 14, listeningTimeArtwork],
                  ].map(([id, field, icon, number, artwork]) => (
                    <RecordCollectionCard key={id} className="calendar-lifetime-stat"
                      artwork={artwork} number={number} isUnlocked={recordUnlocks[id]} fallbackIcon={icon}
                      {...tierProps(id)}
                      label={t(`accountActivity.stats.${id}`)}
                      targetValue={formatRecordTarget(id)}
                      value={id === 'listeningTime'
                        ? formatAchievementDuration(Number(accountSummary?.[field]) || 0)
                        : formatCompactInteger(accountSummary?.[field])} />
                  ))}
                </div>
              </section>
              <section className="calendar-cumulative-records" aria-labelledby="calendar-milestones-title">
                <h2 id="calendar-milestones-title">{t('accountActivity.calendar.milestones')}</h2>
                <div className="calendar-lifetime-stats">
                  {[
                    ['level100', 'level', Star, 15],
                    ['graduation1000', 'graduatedCardCount', Trophy, 16],
                    ['membershipYear', 'accountAgeDays', CalendarDays, 17],
                  ].map(([id, field, icon, number]) => (
                    <RecordCollectionCard key={id} className="calendar-lifetime-stat"
                      artwork={additionalRecordArtwork[id]} number={number} isUnlocked={recordUnlocks[id]} fallbackIcon={icon}
                      {...tierProps(id)}
                      label={t(`accountActivity.stats.${id}`)} targetValue={formatRecordTarget(id)}
                      value={id === 'membershipYear'
                        ? t('accountActivity.stats.daysValue', undefined, { count: accountSummary?.[field] || 0 })
                        : formatCompactInteger(accountSummary?.[field])} />
                  ))}
                </div>
              </section>
              <section className="calendar-records" aria-labelledby="calendar-achievements-title">
                <h2 id="calendar-achievements-title">{t('accountActivity.calendar.focusedTrainingRecords')}</h2>
                <div className="calendar-record-grid">
                  <RecordCollectionCard
                    className="calendar-record-card"
                    artwork={bestStreakArtwork}
                    number={5}
                    isUnlocked={recordUnlocks[RECORD_ACHIEVEMENT_IDS.MAX_STREAK]}
                    targetValue={formatRecordTarget(RECORD_ACHIEVEMENT_IDS.MAX_STREAK)}
                    fallbackIcon={Flame}
                    series="focus"
                    label={t('accountActivity.user.maxDays')}
                    value={achievements.maxContinuousPracticeDays || 0}
                  />
                  <RecordCollectionCard
                    className="calendar-record-card"
                    artwork={bestDayScoreArtwork}
                    number={6}
                    isUnlocked={recordUnlocks[RECORD_ACHIEVEMENT_IDS.MAX_DAILY_SCORE]}
                    targetValue={formatRecordTarget(RECORD_ACHIEVEMENT_IDS.MAX_DAILY_SCORE)}
                    fallbackIcon={Star}
                    series="focus"
                    label={t('accountActivity.user.maxScore')}
                    value={formatCompactInteger(achievements.maxScorePerDay || 0)}
                  />
                  <RecordCollectionCard
                    className="calendar-record-card"
                    artwork={bestDayTimeArtwork}
                    number={7}
                    isUnlocked={recordUnlocks[RECORD_ACHIEVEMENT_IDS.MAX_DAILY_TIME]}
                    targetValue={formatRecordTarget(RECORD_ACHIEVEMENT_IDS.MAX_DAILY_TIME)}
                    fallbackIcon={Clock3}
                    series="focus"
                    label={t('accountActivity.user.maxTime')}
                    valueVariant="time"
                    value={formatAchievementDuration(achievements.maxPracticeTimePerDay || 0)}
                  />
                </div>
                <h3>{t('accountActivity.user.tournamentRecord')}</h3>
                <div className="calendar-record-grid">
                  <RecordCollectionCard
                    className="calendar-record-card"
                    artwork={tournamentScoreArtwork}
                    number={8}
                    isUnlocked={recordUnlocks[RECORD_ACHIEVEMENT_IDS.TOURNAMENT_SCORE]}
                    targetValue={formatRecordTarget(RECORD_ACHIEVEMENT_IDS.TOURNAMENT_SCORE)}
                    fallbackIcon={Trophy}
                    series="tournament"
                    label={t('accountActivity.user.maxTournamentScore')}
                    value={formatCompactInteger(tournament.maxTournamentScore || 0)}
                  />
                  <RecordCollectionCard
                    className="calendar-record-card"
                    artwork={tournamentRoundArtwork}
                    number={9}
                    isUnlocked={recordUnlocks[RECORD_ACHIEVEMENT_IDS.TOURNAMENT_ROUND]}
                    targetValue={formatRecordTarget(RECORD_ACHIEVEMENT_IDS.TOURNAMENT_ROUND)}
                    fallbackIcon={Crown}
                    series="tournament"
                    label={t('accountActivity.user.bestRound')}
                    valueVariant="text"
                    value={formatBestRound(tournament)}
                  />
                  <RecordCollectionCard
                    className="calendar-record-card"
                    artwork={tournamentEntriesArtwork}
                    number={10}
                    isUnlocked={recordUnlocks[RECORD_ACHIEVEMENT_IDS.TOURNAMENT_ENTRIES]}
                    targetValue={formatRecordTarget(RECORD_ACHIEVEMENT_IDS.TOURNAMENT_ENTRIES)}
                    fallbackIcon={CalendarDays}
                    series="tournament"
                    label={t('accountActivity.user.attended')}
                    value={tournament.attendedCount || 0}
                  />
                  <RecordCollectionCard
                    className="calendar-record-card"
                    artwork={tournamentWinRateArtwork}
                    number={11}
                    isUnlocked={recordUnlocks[RECORD_ACHIEVEMENT_IDS.TOURNAMENT_WIN_RATE]}
                    targetValue={formatRecordTarget(RECORD_ACHIEVEMENT_IDS.TOURNAMENT_WIN_RATE)}
                    fallbackIcon={Star}
                    series="tournament"
                    label={t('accountActivity.user.winRate')}
                    valueVariant="text"
                    value={formatWinRate(tournament)}
                  />
                </div>
              </section>
            </>
          )}
        </section>
      )}

      {activeTab === 'pace' && (
        <section className="calendar-tab-panel calendar-pace-panel" role="tabpanel">
          {summaryLoaded && <GoalJourneyPanel summary={accountSummary || {}} />}
        </section>
      )}

      {activeTab === 'calendar' && (
        <section className="calendar-tab-panel calendar-calendar-panel" role="tabpanel">
          <MonthNavigation year={year} month={month} today={protectionState?.today} onPrevious={handlePreviousMonth} onNext={handleNextMonth} onYearChange={(nextYear) => handleMonthChange(month, nextYear)} onMonthChange={handleMonthChange} />
          <div className="calendar-date-range">
            <DateRangeSelector value={dateRange} onChange={setDateRange} showTitle={false} />
          </div>
          <div className="calendar-metric-legend">
            <span><Clock3 size={14} aria-hidden="true" />{t('accountActivity.calendar.dayTimeLegend')}</span>
            <span><Trophy size={14} aria-hidden="true" />{t('accountActivity.calendar.earnedScore')}</span>
            {protectionState?.enabled && protectionState.protection?.month === `${year}-${String(month).padStart(2, '0')}` && (
              <div className="calendar-protection-summary" role="group" aria-label={t('streakProtection.title')}>
                <span><Umbrella size={16} aria-hidden="true" />{t('streakProtection.vacationRemaining', undefined, protectionState.protection.vacation)}</span>
                <span><ShieldCheck size={16} aria-hidden="true" />{t('streakProtection.superpassRemaining', undefined, protectionState.protection.superpass)}</span>
              </div>
            )}
          </div>
          {isCalendarLoading && <div className="calendar-loading" role="status"><p>{t('accountActivity.calendar.loadingCalendar')}</p></div>}
          {calendarError && (
            <div className="calendar-error" role="alert">
              <p>{calendarError}</p>
              <button type="button" onClick={() => setCalendarReloadToken((token) => token + 1)}>{t('accountActivity.retry')}</button>
            </div>
          )}
          {!isCalendarLoading && !calendarError && monthlyData && (
            <>
              <CalendarGrid year={year} month={month} dailyStats={monthlyData} today={protectionState?.today}
                protectionDays={protectionState?.protectionDays || []}
                studyDates={protectionState?.studyDates}
                selectedDate={selectedDate} onSelectDate={protectionState?.enabled ? selectDate : undefined}
                renderDateMenu={(date, protection, stats) => <VacationDateMenu date={date} protection={protection} today={protectionState?.today}
                  hasStudied={Boolean(protectionState?.studyDates?.includes(date) || stats?.totalSeconds > 0)}
                  remaining={protectionState?.protection?.vacation?.remaining ?? 0} isBusy={vacationBusy} error={vacationError}
                  onAction={updateVacation} onClose={closeDateMenu} />} />
              {isMonthRange && <MonthlySummary summary={monthlySummary} />}
            </>
          )}
          {!isMonthRange && isRangeValid && (
            <>
              {isCustomRangeLoading && <p className="calendar-inline-status" role="status">{t('accountActivity.calendar.loadingRange')}</p>}
              {customRangeError && <div className="calendar-error" role="alert"><p>{customRangeError}</p><button type="button" onClick={() => setRangeReloadToken((token) => token + 1)}>{t('accountActivity.retry')}</button></div>}
              {!isCustomRangeLoading && !customRangeError && customRangeStats?.beginDate === dateRange.beginDate && customRangeStats?.endDate === dateRange.endDate && (
                <MonthlySummary summary={customRangeStats} title={t('accountActivity.calendar.rangeSummary', undefined, dateRange)} />
              )}
            </>
          )}
          {isMonthRange && isPaceLoading && <p className="calendar-inline-status" role="status">{t('accountActivity.calendar.loadingPace')}</p>}
          {isMonthRange && paceError && <div className="calendar-error" role="alert"><p>{paceError}</p><button type="button" onClick={() => setPaceReloadToken((token) => token + 1)}>{t('accountActivity.retry')}</button></div>}
          {!isTrendLoading && !trendError && isTrendCurrent && (
            <DailyTrendChart title={t('accountActivity.calendar.dailyTrend')} data={trendData} />
          )}
        </section>
      )}
    </div>
  );
}

export default CalendarPage;
